"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import { ChevronRight, Home } from "lucide-react";
import { cn } from "@/lib/utils";
import { Fragment } from "react";

/**
 * Route name mappings for better breadcrumb labels
 */
const routeNames: Record<string, string> = {
  // Main sections
  dashboard: "Dashboard",
  tiers: "Tiers",
  devis: "Devis",
  commandes: "Commandes",
  factures: "Factures",
  chantiers: "Chantiers",
calendrier: "Calendrier",
  produits: "Produits",
  "commandes-fournisseurs": "Cmd. Fournisseurs",
  parametres: "Paramètres",

  // Actions
  nouveau: "Nouveau",
  modifier: "Modifier",
  edit: "Modifier",

  // Status pages
  "404": "Page non trouvée",
  "500": "Erreur serveur",
};

/**
 * Get a human-readable label for a path segment
 */
function getSegmentLabel(segment: string, isLast: boolean): string {
  // Check if it's a mapped route name
  if (routeNames[segment.toLowerCase()]) {
    return routeNames[segment.toLowerCase()];
  }

  // If it looks like an ID (all uppercase letters and numbers, or just numbers)
  if (/^[A-Z0-9-]+$/.test(segment) || /^\d+$/.test(segment)) {
    // Keep IDs as-is for the last segment, otherwise hide them
    return isLast ? segment : "";
  }

  // Capitalize first letter for unknown segments
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

type BreadcrumbItem = {
  label: string;
  href: string;
  icon?: typeof Home;
};

/**
 * Build breadcrumb items from pathname
 */
function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split("/").filter(Boolean);

  // Always start with home
  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: "Accueil",
      href: "/",
      icon: Home,
    },
  ];

  // Build path incrementally
  let currentPath = "";
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;
    const label = getSegmentLabel(segment, isLast);

    // Only add if we have a label (skip hidden IDs in middle of path)
    if (label) {
      breadcrumbs.push({
        label,
        href: currentPath,
      });
    }
  });

  return breadcrumbs;
}

interface BreadcrumbsProps {
  className?: string;
}

/**
 * Breadcrumbs component - automatically generates breadcrumbs from current route
 */
export function Breadcrumbs({ className }: BreadcrumbsProps) {
  const pathname = usePathname();

  // Don't show breadcrumbs on home page
  if (pathname === "/") {
    return null;
  }

  const breadcrumbs = buildBreadcrumbs(pathname);

  // On mobile, only show last 2 items with ellipsis if more
  const showEllipsis = breadcrumbs.length > 3;

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn("flex items-center gap-1 text-sm", className)}
    >
      {/* Desktop: Show all breadcrumbs */}
      <ol className="hidden items-center gap-1 sm:flex">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const Icon = crumb.icon;

          return (
            <Fragment key={crumb.href}>
              <li className="flex items-center gap-1">
                {isLast ? (
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    {Icon && <Icon className="h-4 w-4" />}
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    href={crumb.href}
                    className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                    {crumb.label}
                  </Link>
                )}
              </li>
              {!isLast && (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </Fragment>
          );
        })}
      </ol>

      {/* Mobile: Show only last 2 items with ellipsis */}
      <ol className="flex items-center gap-1 sm:hidden">
        {showEllipsis && (
          <>
            <li>
              <Link
                href="/"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Home className="h-4 w-4" />
              </Link>
            </li>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <li className="text-muted-foreground">...</li>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </>
        )}
        {breadcrumbs.slice(-2).map((crumb, index, array) => {
          const isLast = index === array.length - 1;
          const Icon = crumb.icon;

          return (
            <Fragment key={crumb.href}>
              <li className="flex items-center gap-1">
                {isLast ? (
                  <span className="flex items-center gap-1.5 font-medium text-foreground max-w-[150px] truncate">
                    {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    href={crumb.href}
                    className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                    {crumb.label}
                  </Link>
                )}
              </li>
              {!isLast && (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Breadcrumbs with custom items (for special cases)
 */
interface CustomBreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

export function CustomBreadcrumbs({ items, className }: CustomBreadcrumbsProps) {
  return (
    <nav
      aria-label="Breadcrumb"
      className={cn("flex items-center gap-1 text-sm", className)}
    >
      <ol className="flex items-center gap-1">
        <li>
          <Link
            href="/"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Home className="h-4 w-4" />
          </Link>
        </li>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <Fragment key={item.href}>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <li>
                {isLast ? (
                  <span className="font-medium text-foreground">{item.label}</span>
                ) : (
                  <Link
                    href={item.href}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
